/*
ChileAutos Form Component for Publication Manager
Integrated form with nested tabs for vehicle details, images, and video
Autor: Assistant
Fecha: 2025-01-23
*/
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '@radix-ui/themes'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import Vehicle from '../interfaces/Vehicle'
import useChaFetcher from '../hooks/useChaFetcher'
import { useAuth } from '../hooks/useAuth'
import useChaPublisher from '../hooks/useChaPublisher'
import { validateFields, Rule } from '../utils/validateForm'
import ChaInputVehicle from '../interfaces/ChaInputVehicle'
import usePublicationDataSaver from '../hooks/usePublicationDataSaver'
import useFileUpload, { UserOrganization } from '../hooks/useFileUpload'
import useImagesReorder from '../hooks/useImagesReorder'
import useImageSelection from '../hooks/useImageSelection'
import useImageBulkDownload from '../hooks/useImageBulkDownload'
import { makeSmallImage } from '../utils/makeImage'
import { BranchDto } from '../interfaces/Branch'
import { autoSelectColor } from '../utils/colorMappingHelpers'

import { SelectField } from './SelectField'
import InputTextArea from './InputTextArea'
import { Info } from './MessageInfo'
import DisplayError from './DisplayError'
import ConfirmationModal from './ConfirmationModal'
import ImageSelectionToolbar from './ImageSelectionToolbar'
import ImageLightbox from './ImageLightbox'
import InputField from './InputField'
import ChileAutosProducts from './ChileAutosProducts'

interface ChaData {
  type: string
  body: string
  brand: string
  model: string
  year: number
  fuel: string
  gearbox: string
  color: string
  comments: string
  title: string
}

interface ChileAutosFormProps {
  vehicle: Vehicle
  branch: BranchDto | null
  onPublishSuccess: () => void
  onImageUpload?: (vehicleId: string) => void
}

const SortableItem = ({
  id,
  selectionMode = false,
  isSelected = false,
  onSelectionChange,
  onImageClick,
}: {
  id: string
  selectionMode?: boolean
  isSelected?: boolean
  onSelectionChange?: (id: string) => void
  onImageClick?: () => void
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  }

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('ChileAutos - Image clicked, selectionMode:', selectionMode, 'onImageClick exists:', !!onImageClick)

    if (!selectionMode && onImageClick) {
      console.log('ChileAutos - Calling onImageClick')
      onImageClick()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-2 flex items-center justify-center rounded-md border bg-white p-2 shadow-sm relative ${
        selectionMode && isSelected ? 'ring-2 ring-orange-500' : ''
      }`}
    >
      {/* Drag handle - only visible when not in selection mode */}
      {!selectionMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 right-2 z-10 bg-gray-700/80 hover:bg-gray-600/80 text-white rounded p-1 cursor-grab active:cursor-grabbing"
          title="Arrastrar para reordenar"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 3h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm0 4h2v2H9zm4-12h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2zm0 4h2v2h-2z" />
          </svg>
        </div>
      )}

      {/* Selection checkbox */}
      {selectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            className="w-5 h-5 rounded border-gray-300 bg-white shadow-sm focus:ring-2 focus:ring-orange-500"
            checked={isSelected}
            onChange={() => onSelectionChange?.(id)}
          />
        </div>
      )}

      <img
        src={makeSmallImage(id)}
        alt="Vehicle"
        className="h-[256px] w-[247px] rounded-md object-cover cursor-pointer transition-opacity duration-200 hover:opacity-90"
        onClick={handleImageClick}
        title="Clic para ver en pantalla completa"
      />
    </div>
  )
}

const ChileAutosForm: React.FC<ChileAutosFormProps> = ({ vehicle, branch, onPublishSuccess, onImageUpload }) => {
  const { userData } = useAuth()
  const [activeTab, setActiveTab] = useState<'details' | 'images' | 'products'>('details')

  // Helper function to create title (same as in Cha.tsx)
  const makeTitle = (vehicle: Vehicle) => {
    return `${vehicle.year} ${vehicle.brand} ${vehicle.model} ${vehicle.version}`
  }

  // Form data state
  const [formData, setFormData] = useState<ChaData>({
    type: vehicle.type,
    body: vehicle.body,
    brand: vehicle.brand,
    model: vehicle.model,
    year: vehicle.year,
    fuel: vehicle.fuel,
    gearbox: vehicle.gearbox,
    color: vehicle.color,
    // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
    comments: vehicle.promo?.message || ' ',
    title: makeTitle(vehicle),
  })

  // Data fetching hooks
  const { chaBrands, chaTypes, chaModels, chaBodies, chaFuel, chaGearbox, chaColor } = useChaFetcher()

  // Publishing hooks
  const { chaPublish, chaSetPublished } = useChaPublisher()
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  // State for dropdowns
  const [brands, setBrands] = useState<string[]>([])
  const [types, setTypes] = useState<string[]>([])
  const [bodies, setBodies] = useState<string[]>([])
  const [models, setModels] = useState<string[]>([])
  const [fuel, setFuel] = useState<string[]>([])
  const [gearbox, setGearbox] = useState<string[]>([])
  const [color, setColor] = useState<string[]>([])

  // Image management
  const [publicImages, setPublicImages] = useState<string[]>(vehicle.pictures?.public ?? [])
  const { imagesReorder } = useImagesReorder()
  const [isOrderSaveDisabled, setOrderSaveDisabled] = useState<boolean>(true)

  // Bulk download and delete functionality
  const { downloadMultipleImages, downloadProgress, deleteMultipleImages, deleteProgress } = useImageBulkDownload()

  // State for delete confirmation
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState<boolean>(false)
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

  // Bulk delete handler with confirmation
  const handleBulkDeleteRequest = useCallback((imageUrls: string[]) => {
    setImagesToDelete(imageUrls)
    setDeleteConfirmationOpen(true)
  }, [])

  // Image selection functionality (defined before handlers that use it)
  const imageSelection = useImageSelection({
    images: publicImages,
    onBulkDownload: downloadMultipleImages,
    onBulkDelete: handleBulkDeleteRequest,
  })

  const handleBulkDeleteConfirm = useCallback(async () => {
    setDeleteConfirmationOpen(false)
    const result = await deleteMultipleImages(imagesToDelete, vehicle._id, false, (deletedImageUrl) => {
      // Update local state immediately for each successful deletion
      setPublicImages((prevImages) => prevImages.filter((url) => url !== deletedImageUrl))
    })

    if (result && result.success > 0) {
      // Clear selection after successful deletions
      imageSelection.resetSelection()

      // Trigger image upload callback to refresh parent state
      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }

    setImagesToDelete([])
  }, [imagesToDelete, deleteMultipleImages, vehicle._id, onImageUpload, imageSelection])

  const handleBulkDeleteCancel = useCallback(() => {
    setDeleteConfirmationOpen(false)
    setImagesToDelete([])
  }, [])

  // Lightbox functions
  const openImageInLightbox = (index: number) => {
    console.log('ChileAutos - Opening lightbox with index:', index, 'Total images:', publicImages.length)
    setLightboxInitialIndex(index)
    setIsLightboxOpen(true)
  }

  const closeLightbox = () => {
    console.log('ChileAutos - Closing lightbox')
    setIsLightboxOpen(false)
  }

  // Enhanced validation and UI state
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(false)
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)
  const [saveMessage, setSaveMessage] = useState<string>('')

  // Notification system (Level 2 - Standard)
  const [publishMessage, setPublishMessage] = useState<string>('')
  const [publishTrigger, setPublishTrigger] = useState<number>(0)
  const [isPublishing, setIsPublishing] = useState<boolean>(false)

  // Este formulario se abre desde la pantalla de publicados, sobre vehículos que
  // en su mayoría ya tienen aviso. Se deriva del vehículo en cada render para que
  // no quede desfasado después de publicar.
  const isPublishedOnChileAutos = !!vehicle.external_chileautos?.identifier

  // Lightbox state for enhanced image viewing
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0)

  const userOrganization: UserOrganization | null = userData
    ? {
        organization_id: vehicle.organization,
        user_id: userData?.user?._id ?? '',
        vehicle_id: vehicle._id,
        registry: '0',
        attachment: '0',
      }
    : null

  const { isUploading, handleFileChangeEx } = useFileUpload(userOrganization!, (newImageUrls?: string[]) => {
    if (newImageUrls && newImageUrls.length > 0) {
      setPublicImages((prevImages) => [...prevImages, ...newImageUrls])

      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  })

  // Main initialization effect with timing control (same as vehicle detail page)
  useEffect(() => {
    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      if (!userData?.organization?.external?.chileautos?.enabled) {
        setDisabled(true)
        setExternalApisComplete(true)

        return
      }

      // Step 1: Fetch all dropdown data in parallel
      const [brandsData, typesData, bodiesData, fuelData, gearboxData, colorData] = await Promise.all([
        chaBrands(),
        chaTypes(),
        chaBodies(),
        chaFuel(),
        chaGearbox(),
        chaColor(),
      ])

      if (brandsData) {
        const filteredBrands = brandsData.filter((b: string) => b.length > 1 && !Number(b))
        setBrands(filteredBrands)
      }

      if (typesData) {
        const typesArray = Object.values(typesData)
        typesArray.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setTypes(typesArray)
      }

      if (bodiesData) {
        const bodiesArray = Object.values(bodiesData)
        bodiesArray.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setBodies(bodiesArray)
      }

      if (fuelData) {
        setFuel(fuelData)
      }

      if (gearboxData) {
        setGearbox(gearboxData)
      }

      if (colorData) {
        colorData.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        setColor(colorData)

        // Auto-select the correct color based on vehicle color
        const colorSelection = autoSelectColor(vehicle.color, colorData, 'chileautos')

        if (colorSelection.selectedColor) {
          setFormData((prev) => ({ ...prev, color: colorSelection.selectedColor! }))
        }
      }

      // Step 2: Load draft data after API data is loaded
      await autoLoadDraftData()

      setExternalApisComplete(true)
      setIsDraftLoading(false)
    }

    initializeComponentData()
  }, [userData])

  // Models effect - fetch models when brand changes
  useEffect(() => {
    const fetchModelsAsync = async () => {
      if (formData.brand) {
        const modelsData = await chaModels(formData.brand)

        if (modelsData) {
          setModels(modelsData)
          // REMOVED: Model validation that clears field
          // User will select valid model from dropdown
          // This preserves models that come from externals/saved data
        }
      } else {
        setModels([])
      }
    }
    fetchModelsAsync()
  }, [formData.brand])

  // Auto-load draft data (same as vehicle detail page)
  const autoLoadDraftData = async () => {
    const result = await loadPublicationData<ChaData>(vehicle._id, 'chileautos')

    if (result.success && result.data) {
      setFormData((prev) => ({ ...prev, ...result.data }))
    }
  }

  // Input handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear errors when user starts typing
    if (errors[name]) {
      const updatedErrors = { ...errors }
      delete updatedErrors[name]
      setErrors(updatedErrors)
    }
  }

  // Brand change handler - clear model when brand changes
  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      model: '', // Clear model when brand changes
    }))

    // Clear errors when user starts typing
    if (errors[name]) {
      const updatedErrors = { ...errors }
      delete updatedErrors[name]
      setErrors(updatedErrors)
    }
  }

  // Draft saving
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    const result = await savePublicationData(vehicle._id, 'chileautos', formData as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Image management
  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setPublicImages((items) => {
        const oldIndex = items.indexOf(active.id as string)
        const newIndex = items.indexOf(over.id as string)
        const newOrder = arrayMove(items, oldIndex, newIndex)
        setOrderSaveDisabled(false)

        return newOrder
      })
    }
  }

  const handleSaveOrder = async () => {
    const resp = await imagesReorder(publicImages, vehicle._id, false)

    if (resp) {
      setOrderSaveDisabled(true)

      if (onImageUpload) {
        onImageUpload(vehicle._id)
      }
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (userOrganization) {
      await handleFileChangeEx(event)
    }
  }

  // Publishing logic
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    // Existing validation and publish logic (unchanged)
    if (!userData?.organization) {
      setPublishMessage('❌ Error: Usuario no autenticado')
      setPublishTrigger(Date.now())

      return
    }

    if (!branch) {
      setPublishMessage('❌ Error: No se encontró la sucursal')
      setPublishTrigger(Date.now())

      return
    }

    // Apply same validation as Cha.tsx using resolved values
    const resolvedValues = {
      type: formData.type || vehicle.type_format,
      body: formData.body || vehicle.body_format,
      brand: formData.brand || vehicle.brand,
      model: formData.model || vehicle.model,
      fuel: formData.fuel || vehicle.fuel_format,
      gearbox: formData.gearbox || vehicle.gearbox_format,
      color: formData.color || vehicle.color,
      title: formData.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
    }

    const validationErrors = validateFields([
      { name: 'type', value: resolvedValues.type, rule: Rule.REQUIRED },
      { name: 'body', value: resolvedValues.body, rule: Rule.REQUIRED },
      { name: 'brand', value: resolvedValues.brand, rule: Rule.REQUIRED },
      { name: 'model', value: resolvedValues.model, rule: Rule.REQUIRED },
      { name: 'fuel', value: resolvedValues.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: resolvedValues.gearbox, rule: Rule.REQUIRED },
      { name: 'color', value: resolvedValues.color, rule: Rule.REQUIRED },
      { name: 'title', value: resolvedValues.title, rule: Rule.REQUIRED },
    ])

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setPublishMessage(`❌ Error de validación: ${Object.values(validationErrors).join(', ')}`)
      setPublishTrigger(Date.now())

      return
    }

    // Check for pictures
    const pictures = publicImages.length > 0 ? publicImages : vehicle.pictures?.public || []

    if (pictures.length === 0) {
      setPublishMessage('❌ Error: El vehículo debe tener al menos una imagen pública')
      setPublishTrigger(Date.now())

      return
    }

    try {
      setIsPublishing(true)

      const chas: ChaInputVehicle = {
        ...vehicle,
        ...formData,
        organization: userData.organization,
        pictures: pictures,
        // Se manda el identificador del aviso existente, si lo hay. Iba fijo en
        // '' y la API entiende eso como "aviso nuevo": generaba otro uuid, creaba
        // un aviso duplicado en ChileAutos y después chaSetPublished reemplazaba
        // el identificador guardado, dejando el aviso original vivo y sin
        // referencia — imposible de despublicar desde Veekls. Con el
        // identificador, la API actualiza el aviso que ya existe, que es lo que
        // ya hacía la publicación masiva.
        inventoryIdentifier: vehicle.external_chileautos?.identifier ?? '',
        characteristics: vehicle.characteristics || [],
      }

      const result = await chaPublish(chas)

      if (result?.ok) {
        const respSetPublished = await chaSetPublished(
          vehicle._id,
          userData?.profile?._id ?? '',
          result.inventoryIdentifier ?? ''
        )

        if (respSetPublished === 'OK') {
          setPublishMessage('✅ Vehículo guardado y publicado exitosamente en ChileAutos')
          setPublishTrigger(Date.now())
          onPublishSuccess()
        } else {
          setPublishMessage('❌ Error al marcar como publicado')
          setPublishTrigger(Date.now())
        }
      } else {
        setPublishMessage('❌ Error al publicar en ChileAutos')
        setPublishTrigger(Date.now())
      }
    } catch (error) {
      setPublishMessage(`❌ Error al publicar: ${error instanceof Error ? error.message : 'Error desconocido'}`)
      setPublishTrigger(Date.now())
    } finally {
      setIsPublishing(false)
    }
  }

  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  const beforePublish = async () => {
    // Save draft data first before publishing
    console.log('Saving draft data before publishing to ChileAutos...')
    const saveResult = await savePublicationData(
      vehicle._id,
      'chileautos',
      formData as unknown as Record<string, unknown>
    )

    if (!saveResult.success) {
      setPublishMessage(`❌ Error al guardar datos: ${saveResult.message}`)
      setPublishTrigger(Date.now())

      return
    }

    console.log('✅ Draft data saved successfully, proceeding with ChileAutos publication')

    // Un aviso nuevo se publica directo. Uno que ya está en línea pide
    // confirmación, porque volver a publicar sobrescribe el aviso existente.
    if (isPublishedOnChileAutos) {
      setConfirmationModalOpen(true)

      return
    }

    await handlePublishConfirm()
  }

  if (!userOrganization) {
    return <div>Loading...</div>
  }

  return (
    <div className="space-y-4">
      {/* Nested Tab Navigation for ChileAutos */}
      <div className="border-b border-gray-200 mb-4">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('details')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Detalles del Vehículo
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'images'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Imágenes Públicas ({publicImages.length})
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'products'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Destacados
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'details' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Detalles del Vehículo - ChileAutos</h3>

            {/* Show loading message (same as vehicle detail page) */}
            {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
              !(saveMessage && saveMessage.startsWith('✅')) && (
                <div className="flex justify-center mt-2">
                  <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
                </div>
              )}

            {!isDraftLoading && minimumTimeElapsed && externalApisComplete && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <InputField
                  label="Título"
                  name="title"
                  value={formData.title}
                  type="text"
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Tipo"
                  name="type"
                  value={formData.type}
                  options={types.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Carrocería"
                  name="body"
                  value={formData.body}
                  options={bodies.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Marca"
                  name="brand"
                  value={formData.brand}
                  options={brands.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleBrandChange}
                  errors={errors}
                />
                <SelectField
                  label="Modelo"
                  name="model"
                  value={formData.model}
                  options={models.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <InputField
                  label="Año"
                  name="year"
                  value={formData.year}
                  type="number"
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Combustible"
                  name="fuel"
                  value={formData.fuel}
                  options={fuel.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Transmisión"
                  name="gearbox"
                  value={formData.gearbox}
                  options={gearbox.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <SelectField
                  label="Color"
                  name="color"
                  value={formData.color}
                  options={color.reduce((acc, cur) => ({ ...acc, [cur]: cur }), {})}
                  handleInputChange={handleInputChange}
                  errors={errors}
                />
                <div className="sm:col-span-2 lg:col-span-3">
                  <InputTextArea
                    label="Descripción"
                    name="comments"
                    value={formData.comments}
                    handleInputChange={handleInputChange}
                    errors={errors}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'images' && (
          <div className="space-y-4">
            {/* Bulk Selection Toolbar */}
            {publicImages.length > 0 && (
              <ImageSelectionToolbar
                isSelectionMode={imageSelection.isSelectionMode}
                selectedCount={imageSelection.selectedCount}
                totalCount={imageSelection.totalCount}
                onToggleSelectionMode={imageSelection.toggleSelectionMode}
                onSelectAll={imageSelection.selectAll}
                onSelectNone={imageSelection.selectNone}
                onBulkDownload={imageSelection.handleBulkDownload}
                canDownload={imageSelection.canDownload}
                downloadProgress={downloadProgress}
                onBulkDelete={imageSelection.handleBulkDelete}
                canDelete={imageSelection.canDelete}
                showDeleteOption={true}
                deleteProgress={deleteProgress}
              />
            )}

            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Imágenes Públicas - ChileAutos</h3>
              {!imageSelection.isSelectionMode && (
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="cha-file-upload"
                    className={`cursor-pointer rounded bg-orange-500 px-4 py-2 text-white hover:bg-orange-600 ${isUploading && 'cursor-not-allowed opacity-50'}`}
                  >
                    {isUploading ? 'Cargando...' : 'Agregar imagen...'}
                  </label>
                  <input
                    id="cha-file-upload"
                    type="file"
                    multiple={true}
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button onClick={handleSaveOrder} disabled={isOrderSaveDisabled}>
                    Guardar orden
                  </Button>
                </div>
              )}
            </div>

            {/* Enhanced Grid Layout with Lightbox Integration */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={publicImages} strategy={verticalListSortingStrategy}>
                  {publicImages.map((id, index) => (
                    <div key={id} className="relative">
                      <SortableItem
                        id={id}
                        selectionMode={imageSelection.isSelectionMode}
                        isSelected={imageSelection.isImageSelected(id)}
                        onSelectionChange={imageSelection.handleSelectionChange}
                        onImageClick={() => openImageInLightbox(index)}
                      />
                    </div>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            {publicImages.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No hay imágenes públicas. Agregue algunas para mejorar la publicación.</p>
              </div>
            )}
          </div>
        )}

        {/* onImageUpload es el "recargar sin cerrar" del padre; onPublishSuccess cerraría el formulario. */}
        {activeTab === 'products' && (
          <ChileAutosProducts vehicle={vehicle} onChange={() => onImageUpload?.(vehicle._id)} />
        )}
      </div>

      {/* Save message area (same as vehicle detail page) */}
      {(saveMessage || saveError) && (
        <div className="flex justify-center mt-2">
          <div className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMessage || saveError}
          </div>
        </div>
      )}

      {/* Notification display */}
      {publishMessage && (
        <div className="flex justify-center mt-4">
          <Info
            message={publishMessage}
            trigger={publishTrigger}
            onTimeout={() => setPublishMessage('')}
            noCenter={true}
          />
        </div>
      )}

      {/* Action Buttons: guardar y publicar no aplican a la pestaña de destacados */}
      {activeTab !== 'products' && (
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-center gap-4">
          <Button
            onClick={handleSaveDraft}
            disabled={savingLoading}
            className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200"
          >
            {savingLoading ? 'Guardando...' : 'Guardar Borrador'}
          </Button>
          <Button
            onClick={beforePublish}
            disabled={disabled || isPublishing}
            className="bg-orange-600 hover:bg-orange-700 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPublishing
              ? isPublishedOnChileAutos
                ? 'Republicando...'
                : 'Publicando...'
              : isPublishedOnChileAutos
                ? 'Republicar en ChileAutos'
                : 'Publicar en ChileAutos'}
          </Button>
        </div>
      )}

      {/* Error Display */}
      {Object.keys(errors).length > 0 && <DisplayError msg={Object.values(errors).join(', ')} />}

      {/* Confirmation Modal */}
      {confirmationModalOpen && (
        <ConfirmationModal
          message={
            isPublishedOnChileAutos
              ? 'Este vehículo ya está publicado en ChileAutos. Al continuar se actualizará el aviso que está en línea con los datos de este formulario. ¿Desea republicarlo?'
              : '¿Está seguro de que desea publicar este vehículo en ChileAutos?'
          }
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {deleteConfirmationOpen && (
        <ConfirmationModal
          message={`¿Estás seguro de eliminar ${imagesToDelete.length} imagen${imagesToDelete.length > 1 ? 'es' : ''}? Esta acción no se puede deshacer.`}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={handleBulkDeleteCancel}
        />
      )}

      {/* Enhanced Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        imageUrls={publicImages}
        initialIndex={lightboxInitialIndex}
        onDelete={(imageUrl) => {
          // Remove from local state immediately
          setPublicImages((prevImages) => prevImages.filter((url) => url !== imageUrl))

          // Trigger data refresh
          if (onImageUpload) {
            onImageUpload(vehicle._id)
          }
        }}
        canDelete={true}
      />
    </div>
  )
}

export default ChileAutosForm
